/**
 * Canonical phone parsing, validation and formatting — client side.
 *
 * ⚠️ MIRROR of `src/utils/phoneFormat.js` in the backend repo
 * (danthemank/athletics-club-platform). The two must agree, or a number the
 * form accepts gets rejected by the API and the member is stuck with a valid
 * number and an error message. Both test suites assert the SAME vector table —
 * `PHONE_VECTORS` in each repo's phoneFormat test. That table is the guard
 * against drift; change one side and you must change both.
 *
 * The mask in `components/ui/PhoneInput.tsx` makes malformed input hard to
 * TYPE, but paste, browser autofill, password managers and the public API all
 * reach the same column without ever touching it. So this validates too, and
 * the backend validates again. Three layers, none of them redundant.
 *
 * What none of them catch: a transposed but well-formed 10-digit number. That
 * passes every check here and still texts a stranger. Only confirming the
 * number defends against that.
 */

export interface CountryConfig {
  iso: string;
  name: string;
  callingCode: string;
  nationalLength: number;
  nanp: boolean;
}

/**
 * Supported dialling plans.
 *
 * US-only deliberately: all 4,627 stored numbers across both tenants are US.
 * A country dropdown today would be a field staff could get wrong, guarding a
 * case that does not exist. Country is a parameter so adding one later is a
 * config change plus a `<Select>`, not a rewrite.
 */
export const COUNTRIES: Record<string, CountryConfig> = {
  US: {
    iso: "US",
    name: "United States",
    callingCode: "1",
    nationalLength: 10,
    // NANP: area code and exchange both start 2-9. Rejects 0000000000 and
    // 1234567890, which is most of what placeholder junk actually looks like.
    nanp: true,
  },
};

export const DEFAULT_COUNTRY = "US";

/**
 * Stand-in for "already E.164, some country we hold no plan for". Its calling
 * code is empty because the digits already carry it.
 */
const FOREIGN_E164: CountryConfig = {
  iso: "E164",
  name: "International",
  callingCode: "",
  nationalLength: 0,
  nanp: false,
};

export type InvalidReason =
  | "empty"
  | "too_short"
  | "too_long"
  | "not_diallable"
  | "unsupported_country";

export type ParseResult =
  | { ok: true; national: string; country: CountryConfig }
  | { ok: false; reason: InvalidReason };

/** Every digit in the value, nothing else. "(717) 555-8842" -> "7175558842". */
export function digitsOf(value: unknown): string {
  return String(value ?? "").replace(/\D/g, "");
}

function countryConfig(iso?: string): CountryConfig | null {
  return COUNTRIES[String(iso || DEFAULT_COUNTRY).toUpperCase()] || null;
}

export function parsePhone(value: unknown, iso: string = DEFAULT_COUNTRY): ParseResult {
  const country = countryConfig(iso);
  if (!country) return { ok: false, reason: "unsupported_country" };

  const raw = String(value ?? "").trim();
  if (!raw) return { ok: false, reason: "empty" };

  // A value that already carries an explicit international prefix is treated
  // as E.164 and passed through when it is well formed.
  //
  // The danger was never international numbers — it was INVENTING a country
  // code for a number that had none, which is how "+447700900123" would have
  // become "+1447700900123": a different, plausible, wrong number. Refusing
  // well-formed foreign numbers outright would instead drop a capability the
  // old send path had (Twilio dials them fine) for no safety gain.
  //
  // We cannot check another country's numbering plan without metadata we
  // deliberately do not ship, so the check is E.164's own shape.
  if (raw.startsWith("+")) {
    const withoutPlus = digitsOf(raw);
    if (!withoutPlus.startsWith(country.callingCode)) {
      if (withoutPlus.length < 8 || withoutPlus.length > 15 || withoutPlus.startsWith("0")) {
        return { ok: false, reason: "not_diallable" };
      }
      return { ok: true, national: withoutPlus, country: FOREIGN_E164 };
    }
  }

  let digits = digitsOf(raw);
  if (!digits) return { ok: false, reason: "not_diallable" };

  if (
    digits.length === country.nationalLength + country.callingCode.length &&
    digits.startsWith(country.callingCode)
  ) {
    digits = digits.slice(country.callingCode.length);
  }

  if (digits.length < country.nationalLength) return { ok: false, reason: "too_short" };
  if (digits.length > country.nationalLength) return { ok: false, reason: "too_long" };

  if (country.nanp) {
    const areaStart = digits[0];
    const exchangeStart = digits[3];
    if (areaStart === "0" || areaStart === "1") return { ok: false, reason: "not_diallable" };
    if (exchangeStart === "0" || exchangeStart === "1") {
      return { ok: false, reason: "not_diallable" };
    }
  }

  return { ok: true, national: digits, country };
}

/** E.164 for dialling ("+17175558842"), or null when it cannot be dialled. */
export function toE164(value: unknown, iso: string = DEFAULT_COUNTRY): string | null {
  const parsed = parsePhone(value, iso);
  if (!parsed.ok) return null;
  return `+${parsed.country.callingCode}${parsed.national}`;
}

export function isValidPhone(value: unknown, iso: string = DEFAULT_COUNTRY): boolean {
  return parsePhone(value, iso).ok;
}

/**
 * Display form: "(717) 555-8842". Returns the input unchanged when it cannot
 * be parsed — a display helper must never hide data staff need to see and fix.
 */
export function formatNational(value: unknown, iso: string = DEFAULT_COUNTRY): string {
  const parsed = parsePhone(value, iso);
  if (!parsed.ok) return String(value ?? "");
  // A foreign E.164 number has no national plan here, so there is no grouping
  // we can honestly apply — "(447) 700-900123" would be an invention. Show it
  // in the only form we know is right.
  if (!parsed.country.nanp) return `+${parsed.national}`;
  const d = parsed.national;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

/** A sentence a human can act on, or null when the value is fine. */
export function describeInvalid(value: unknown, iso: string = DEFAULT_COUNTRY): string | null {
  const parsed = parsePhone(value, iso);
  if (parsed.ok) return null;
  switch (parsed.reason) {
    case "empty":
      return "Please enter a phone number.";
    case "too_short":
      return "That number is too short — please include the area code.";
    case "too_long":
      return "That number has too many digits.";
    case "unsupported_country":
      return "Only US phone numbers are supported right now.";
    default:
      return "That doesn't look like a valid US phone number.";
  }
}

/**
 * The national digits of a value, country code shed, capped at the national
 * length.
 *
 * The shedding is the point. "+1 (717) 555-8842" is 11 digits; naively taking
 * the first 10 gives 1717555884, which renders as "(171) 755-5884" — a
 * different, plausible-looking, wrong number. That is what people paste, since
 * it is how phones display a saved contact.
 */
export function toNationalDigits(value: unknown): string {
  const country = COUNTRIES.US;
  let d = digitsOf(value);
  if (
    d.length === country.nationalLength + country.callingCode.length &&
    d.startsWith(country.callingCode)
  ) {
    d = d.slice(country.callingCode.length);
  }
  return d.slice(0, country.nationalLength);
}

/**
 * Progressive display value for a partially-typed US number.
 *
 * Drives the mask: the caller keeps DIGITS in state and renders this. The
 * separators are produced here rather than living in the stored value, so the
 * form can never submit "(717) 555-8842" by accident — which is how the
 * database ended up 74% formatted in the first place.
 *
 * The "(717) 555-8842" mask appears only once all ten digits are there.
 * Short of that the digits show as typed, with a dash after the third, so a
 * seven-digit local number reads "555-0123". Until 2026-10-10 the ten-digit
 * mask was applied while typing: "5550123" became "(555) 012-3", and the many
 * forms that keep this display string in their state SAVED it that way (the
 * backend keeps an incomplete number exactly as sent, see
 * `normalizeStoredPhone`). "555-0123" is also how a stored seven-digit number
 * is shown (`displayStoredPhone` in components/ui/PhoneInput).
 *
 *   ""          -> ""
 *   "717"       -> "717"
 *   "7175"      -> "717-5"
 *   "5550123"   -> "555-0123"
 *   "7175558842"-> "(717) 555-8842"
 */
export function formatPartialNational(digits: string): string {
  const d = digitsOf(digits).slice(0, COUNTRIES.US.nationalLength);
  if (d.length === 0) return "";
  if (d.length === COUNTRIES.US.nationalLength) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  if (d.length <= 3) return d;
  return `${d.slice(0, 3)}-${d.slice(3)}`;
}
